import React, { useState, useMemo } from 'react'
import moment from 'moment'
import {
  DcDiv,
  DcFlexBox,
  DcText,
  DcDateRangePicker,
  colors,
  formatCurrency,
} from 'pixel-base'
import PixelTable from 'pixel-base/components/pixel-table/pixel-table'

interface CityData {
  city: string
  state: string
  country: string
  total_orders: number
  successful_orders: number
  returned_orders: number
  net_revenue: number
  returned_revenue: number
  avg_order_value: number
  unique_customers: number
  return_rate_percentage: number
  success_rate_percentage: number
  revenue_return_rate_percentage: number
  orders_per_customer: number
}

interface TopCitiesDetailPaneProps {
  data: CityData[]
  loading: boolean
  filters: {
    start_date: Date
    end_date: Date
    data_type: string
  }
  onClose: () => void
  onFiltersChange?: (filters: {
    start_date: Date
    end_date: Date
    data_type: string
  }) => void
}

const TopCitiesDetailPane: React.FC<TopCitiesDetailPaneProps> = ({
  data,
  loading,
  filters,
  onClose: _onClose,
  onFiltersChange,
}) => {
  const [sortBy, setSortBy] = useState<'total_orders' | 'net_revenue'>(
    'total_orders',
  )
  const [tableSortBy] = useState<{
    field: string
    direction: 'asc' | 'desc'
  } | null>(null)

  // Date filter state for detail pane - initialize with dashboard filters
  const [detailFilters, setDetailFilters] = useState({
    start_date:
      filters?.start_date || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    end_date: filters?.end_date || new Date(),
    data_type: filters?.data_type || 'daily',
  })

  // Reset detail filters when dashboard filters change
  React.useEffect(() => {
    if (filters) {
      setDetailFilters(filters)
    }
  }, [filters])

  // Sort data based on current sort criteria
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return []

    const sorted = [...data]

    if (sortBy === 'total_orders') {
      sorted.sort((a, b) => b.total_orders - a.total_orders)
    } else if (sortBy === 'net_revenue') {
      sorted.sort((a, b) => b.net_revenue - a.net_revenue)
    }

    return sorted
  }, [data, sortBy])

  // Table sorting
  const tableSortedData = useMemo(() => {
    if (!tableSortBy || !sortedData.length) return sortedData

    return [...sortedData].sort((a, b) => {
      const aValue = a[tableSortBy.field as keyof CityData]
      const bValue = b[tableSortBy.field as keyof CityData]

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return tableSortBy.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return tableSortBy.direction === 'asc'
          ? aValue - bValue
          : bValue - aValue
      }

      return 0
    })
  }, [sortedData, tableSortBy])

  // CSV Download function
  const downloadCSV = () => {
    if (!tableSortedData.length) return

    const headers = [
      'City',
      'State',
      'Country',
      'Total Orders',
      'Successful Orders',
      'Returned Orders',
      'Net Revenue',
      'Returned Revenue',
      'Avg Order Value',
      'Unique Customers',
      'Return Rate %',
      'Success Rate %',
      'Revenue Return Rate %',
      'Orders Per Customer',
    ]

    const csvContent = [
      headers.join(','),
      ...tableSortedData.map((city) =>
        [
          `"${city.city}"`,
          `"${city.state}"`,
          `"${city.country}"`,
          city.total_orders,
          city.successful_orders,
          city.returned_orders,
          city.net_revenue,
          city.returned_revenue,
          city.avg_order_value,
          city.unique_customers,
          city.return_rate_percentage,
          city.success_rate_percentage,
          city.revenue_return_rate_percentage,
          city.orders_per_customer,
        ].join(','),
      ),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute(
      'download',
      `top-cities-${moment().format('YYYY-MM-DD')}.csv`,
    )
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Table columns
  const tableColumns = [
    {
      name: 'City',
      selector: (row: CityData) => row.city,
      width: '12%',
      sortable: true,
      cell: (row: CityData) => (
        <DcText style={{ fontWeight: '600', color: colors.text }}>
          {row.city}
        </DcText>
      ),
    },
    {
      name: 'State',
      selector: (row: CityData) => row.state,
      width: '8%',
      sortable: true,
      cell: (row: CityData) => (
        <DcText style={{ color: colors.grayTextDark }}>{row.state}</DcText>
      ),
    },
    {
      name: 'Total Orders',
      selector: (row: CityData) => row.total_orders,
      width: '8%',
      right: true,
      sortable: true,
      cell: (row: CityData) => (
        <DcText style={{ fontWeight: '600', color: colors.text }}>
          {row.total_orders.toLocaleString()}
        </DcText>
      ),
    },
    {
      name: 'Successful',
      selector: (row: CityData) => row.successful_orders,
      width: '8%',
      right: true,
      sortable: true,
      cell: (row: CityData) => (
        <DcText style={{ fontWeight: '500', color: '#10b981' }}>
          {row.successful_orders.toLocaleString()}
        </DcText>
      ),
    },
    {
      name: 'Returned',
      selector: (row: CityData) => row.returned_orders,
      width: '8%',
      right: true,
      sortable: true,
      cell: (row: CityData) => (
        <DcText style={{ fontWeight: '500', color: '#ef4444' }}>
          {row.returned_orders.toLocaleString()}
        </DcText>
      ),
    },
    {
      name: 'Success Rate',
      selector: (row: CityData) => row.success_rate_percentage,
      width: '8%',
      right: true,
      sortable: true,
      cell: (row: CityData) => (
        <DcText style={{ fontWeight: '500', color: '#10b981' }}>
          {row.success_rate_percentage.toFixed(1)}%
        </DcText>
      ),
    },
    {
      name: 'Net Revenue',
      selector: (row: CityData) => row.net_revenue,
      width: '10%',
      right: true,
      sortable: true,
      cell: (row: CityData) => (
        <DcText style={{ fontWeight: '600', color: '#10b981' }}>
          {formatCurrency(row.net_revenue)}
        </DcText>
      ),
    },
    {
      name: 'Avg Order Value',
      selector: (row: CityData) => row.avg_order_value,
      width: '10%',
      right: true,
      sortable: true,
      cell: (row: CityData) => (
        <DcText style={{ fontWeight: '500', color: colors.grayTextDark }}>
          {formatCurrency(row.avg_order_value)}
        </DcText>
      ),
    },
    {
      name: 'Unique Customers',
      selector: (row: CityData) => row.unique_customers,
      width: '10%',
      right: true,
      sortable: true,
      cell: (row: CityData) => (
        <DcText style={{ fontWeight: '500', color: colors.grayTextDark }}>
          {row.unique_customers.toLocaleString()}
        </DcText>
      ),
    },
  ]

  return (
    <DcDiv style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Panel Header with Controls */}
      <DcDiv
        style={{
          padding: '16px 24px',
          borderBottom: `1px solid ${colors.grayBorderLight}`,
          backgroundColor: 'white',
        }}
      >
        {/* Panel Level Controls */}
        <DcFlexBox
          gap="24px"
          alignItems="center"
          justifyContent="space-between"
        >
          {/* Total Orders/Revenue Toggle */}
          <DcDiv>
            <DcText
              style={{
                fontSize: '12px',
                color: '#6c757d',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Sort By
            </DcText>
            <DcFlexBox
              style={{
                border: `2px solid ${colors.grayBorderLight}`,
                borderRadius: '8px',
                overflow: 'hidden',
                background: 'white',
              }}
            >
              <button
                onClick={() => setSortBy('total_orders')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background:
                    sortBy === 'total_orders' ? colors.primary : 'transparent',
                  color: sortBy === 'total_orders' ? 'white' : colors.text,
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  minWidth: '80px',
                }}
              >
                Total Orders
              </button>
              <button
                onClick={() => setSortBy('net_revenue')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background:
                    sortBy === 'net_revenue' ? colors.primary : 'transparent',
                  color: sortBy === 'net_revenue' ? 'white' : colors.text,
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  minWidth: '80px',
                }}
              >
                Net Revenue
              </button>
            </DcFlexBox>
          </DcDiv>

          {/* Download CSV Button */}
          <DcDiv>
            <DcText
              style={{
                fontSize: '12px',
                color: '#6c757d',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Export
            </DcText>
            <button
              onClick={downloadCSV}
              disabled={!tableSortedData.length}
              style={{
                padding: '10px 20px',
                border: `2px solid ${colors.primary}`,
                background: 'transparent',
                color: colors.primary,
                cursor: tableSortedData.length ? 'pointer' : 'not-allowed',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                opacity: tableSortedData.length ? 1 : 0.5,
                minWidth: '120px',
              }}
              onMouseEnter={(e) => {
                if (tableSortedData.length) {
                  e.currentTarget.style.background = colors.primary
                  e.currentTarget.style.color = 'white'
                }
              }}
              onMouseLeave={(e) => {
                if (tableSortedData.length) {
                  e.currentTarget.style.background = 'transparent'
                  e.currentTarget.style.color = colors.primary
                }
              }}
            >
              Download CSV
            </button>
          </DcDiv>

          {/* Date Filter */}
          <DcDiv>
            <DcText
              style={{
                fontSize: '12px',
                color: '#6c757d',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Date Range
            </DcText>
            <DcDateRangePicker
              size="sm"
              month={2}
              ranges={{
                start_date: detailFilters.start_date,
                end_date: detailFilters.end_date,
              }}
              onApply={() => {
                if (onFiltersChange) {
                  onFiltersChange(detailFilters)
                }
              }}
              onChange={(e) => {
                const newFilters = {
                  start_date: e.range1.startDate,
                  end_date: e.range1.endDate,
                  data_type: e.range1.data_type || 'daily',
                }
                setDetailFilters(newFilters)
                if (onFiltersChange) {
                  onFiltersChange(newFilters)
                }
              }}
            />
          </DcDiv>
        </DcFlexBox>
      </DcDiv>

      {/* Content Area */}
      <DcDiv style={{ flex: 1, padding: '24px 32px', overflow: 'auto' }}>
        {loading ? (
          <DcFlexBox justifyContent="center" alignItems="center" height="100%">
            <DcText>Loading...</DcText>
          </DcFlexBox>
        ) : (
          <DcDiv style={{ height: '100%', overflow: 'auto', width: '100%' }}>
            <PixelTable
              key={`table-${tableSortedData.length}`}
              columns={tableColumns}
              data={tableSortedData}
              pagination={false}
              customStyles={{
                table: {
                  style: {
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    overflow: 'auto',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    minWidth: '100%',
                  },
                },
                tableWrapper: {
                  style: {
                    overflow: 'auto',
                    width: '100%',
                  },
                },
              }}
            />
          </DcDiv>
        )}
      </DcDiv>
    </DcDiv>
  )
}

export default TopCitiesDetailPane
